import ApiService from './api';
import { safeArray } from '../utils/money';

export interface Account {
  id: string;
  currency: string;
  balance: number;
  available_balance: number;
  pending_balance: number;
  type: string;
  status: string;
  created_at: string;
}

export interface Transfer {
  id: string;
  from_account_id: string;
  to_account_id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  created_at: string;
  completed_at?: string;
}

export interface Settlement {
  id: string;
  amount: number;
  currency: string;
  status: string;
  destination_type: string;
  destination_details: any;
  created_at: string;
  processed_at?: string;
}

export interface ConversionRequest {
  from_currency: string;
  to_currency: string;
  amount: number;
}

/**
 * 结算银行账户（GET 返回的脱敏结构）。
 * 账号 / IBAN 已由后端脱敏，如 "•••• 1234"。
 */
export interface SettlementBankAccount {
  configured: boolean;
  account_holder_name?: string;
  account_holder_type?: 'private' | 'company' | string;
  country?: string;
  currency?: string;
  bank_name?: string;
  routing_number?: string;
  swift_code?: string;
  bank_code?: string;
  account_number_masked?: string;
  iban_masked?: string;
}

/**
 * 保存结算银行账户的请求体。IBAN 与 account_number 至少填一个。
 */
export interface SettlementBankAccountPayload {
  account_holder_name: string;
  account_holder_type: 'private' | 'company';
  country: string;
  currency: string;
  iban?: string;
  account_number?: string;
  routing_number?: string;
  swift_code?: string;
  bank_code?: string;
  bank_name?: string;
}

export class TreasuryService {
  /**
   * 获取账户列表
   */
  static async getAccounts(): Promise<Account[]> {
    const response = await ApiService.get<Account[]>('/merchant/treasury/accounts');
    return safeArray<Account>(response.data);
  }

  /**
   * 创建账户
   */
  static async createAccount(data: {
    currency: string;
    type: string;
  }): Promise<Account> {
    const response = await ApiService.post<Account>('/merchant/treasury/accounts', data);
    return response.data;
  }

  /**
   * 获取账户详情
   */
  static async getAccount(id: string): Promise<Account> {
    const response = await ApiService.get<Account>(`/merchant/treasury/accounts/${id}`);
    return response.data;
  }

  /**
   * 更新账户
   */
  static async updateAccount(id: string, data: any): Promise<Account> {
    const response = await ApiService.put<Account>(`/merchant/treasury/accounts/${id}`, data);
    return response.data;
  }

  /**
   * 创建转账
   */
  static async createTransfer(data: {
    from_account_id: string;
    to_account_id: string;
    amount: number;
    currency: string;
    description?: string;
  }): Promise<Transfer> {
    const response = await ApiService.post<Transfer>('/merchant/treasury/transfers', data);
    return response.data;
  }

  /**
   * 获取转账列表
   */
  static async listTransfers(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: Transfer[];
    meta: any;
  }> {
    const params = { page, limit, ...filters };
    const response = await ApiService.get('/merchant/treasury/transfers', params);
    return {
      data: safeArray<Transfer>(response.data),
      meta: response.meta || { total: 0, per_page: 20, current_page: 1, last_page: 1 },
    };
  }

  /**
   * 获取转账详情
   */
  static async getTransfer(id: string): Promise<Transfer> {
    const response = await ApiService.get<Transfer>(`/merchant/treasury/transfers/${id}`);
    return response.data;
  }

  /**
   * 取消转账
   */
  static async cancelTransfer(id: string): Promise<void> {
    await ApiService.post(`/merchant/treasury/transfers/${id}/cancel`);
  }

  /**
   * 货币转换
   */
  static async convertCurrency(data: ConversionRequest): Promise<any> {
    const response = await ApiService.post('/merchant/treasury/convert', data);
    return response.data;
  }

  /**
   * 获取汇率
   */
  static async getExchangeRate(from: string, to: string): Promise<{
    from_currency: string;
    to_currency: string;
    rate: number;
    updated_at: string;
  }> {
    const response = await ApiService.get('/merchant/treasury/exchange/rate', {
      from_currency: from,
      to_currency: to,
    });
    return response.data;
  }

  /**
   * 创建结算
   */
  static async createSettlement(data: {
    amount: number;
    currency: string;
    destination_type: string;
    destination_id?: string;
    destination_details: any;
  }): Promise<Settlement> {
    const response = await ApiService.post<Settlement>('/merchant/treasury/settlements', data);
    return response.data;
  }

  /**
   * MoR 商户一键发起结算：把所有有可用余额的币种各创建一笔结算到银行账户。
   * 后端无需请求体，会按 mode=mor 自动处理。
   * POST /merchant/settlements/mor
   */
  static async createMoRSettlement(): Promise<any> {
    const response = await ApiService.post('/merchant/settlements/mor', {});
    return response.data;
  }

  /**
   * 获取结算银行账户配置（脱敏回显）。
   * GET /merchant/settings/settlements/bank-account
   */
  static async getSettlementBankAccount(): Promise<SettlementBankAccount> {
    const response = await ApiService.get<SettlementBankAccount>(
      '/merchant/settings/settlements/bank-account'
    );
    return response.data;
  }

  /**
   * 保存结算银行账户配置（用于 MoR 结算打款）。
   * PUT /merchant/settings/settlements/bank-account
   */
  static async updateSettlementBankAccount(
    payload: SettlementBankAccountPayload
  ): Promise<SettlementBankAccount> {
    const response = await ApiService.put<SettlementBankAccount>(
      '/merchant/settings/settlements/bank-account',
      payload
    );
    return response.data;
  }

  /**
   * 获取结算列表
   */
  static async listSettlements(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: Settlement[];
    meta: any;
  }> {
    const params = { page, limit, ...filters };
    const response = await ApiService.get('/merchant/treasury/settlements', params);
    return {
      data: safeArray<Settlement>(response.data),
      meta: response.meta || { total: 0, per_page: 20, current_page: 1, last_page: 1 },
    };
  }

  /**
   * 获取结算详情
   */
  static async getSettlement(id: string): Promise<Settlement> {
    const response = await ApiService.get<Settlement>(`/merchant/treasury/settlements/${id}`);
    return response.data;
  }

  /**
   * 取消结算
   */
  static async cancelSettlement(id: string): Promise<void> {
    await ApiService.post(`/merchant/treasury/settlements/${id}/cancel`);
  }

  /**
   * 获取财务流水
   */
  static async listTransactions(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: any[];
    meta: any;
  }> {
    const params = { page, limit, ...filters };
    const response = await ApiService.get('/merchant/treasury/transactions', params);
    return {
      data: safeArray(response.data),
      meta: response.meta || { total: 0, per_page: 20, current_page: 1, last_page: 1 },
    };
  }

  /**
   * 获取交易详情
   */
  static async getTransaction(id: string): Promise<any> {
    const response = await ApiService.get(`/merchant/treasury/transactions/${id}`);
    return response.data;
  }
}

export default TreasuryService;
